/**
 * HubShell — a lightweight internal-tab container for the LCC "Training &
 * Workforce" hubs. Embeds the existing self-contained workforce/LMS pages as
 * tabs. Embedded pages render under the EmbeddedChrome context, so their own
 * PageHeader collapses to a slim actions toolbar and their PageContainer
 * defers scrolling to this shell — no nested page chrome. Sub-tab visibility
 * is role-gated through the shared SUB_TAB_REGISTRY (via usePageSubTabs /
 * GatedTabContent), so e.g. the admin-only "Courses" authoring tab stays
 * hidden from plain staff.
 *
 * The hubs themselves are surfaced only for orgs with the `lms` capability
 * (currently LCC / cbo 350) — see tabAccessRegistry + the sidebar section
 * injection in V2Sidebar.
 */
import React, { useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { GatedTabContent, usePageSubTabs } from '@/components/common/GatedTabContent';
import { PageHeader, EmbeddedChrome } from '@/components/weavein';

export interface HubTab {
  key: string;
  label: string;
  icon: LucideIcon;
  render: () => React.ReactNode;
}

interface HubShellProps {
  pageKey: string;
  title: string;
  subtitle?: string;
  tabs: HubTab[];
}

export default function HubShell({ pageKey, title, subtitle, tabs }: HubShellProps) {
  const accessible = usePageSubTabs(pageKey);
  const visibleTabs = useMemo(
    () => tabs.filter((t) => accessible.includes(t.key)),
    [tabs, accessible],
  );

  // One-shot sub-tab deep link: immersive pages (e.g. the course editor) stash
  // the hub tab to land on before navigating back to the shell.
  const [activeKey, setActiveKey] = useState<string | null>(() => {
    try {
      const stashed = localStorage.getItem(`hub_subtab:${pageKey}`);
      if (stashed) {
        localStorage.removeItem(`hub_subtab:${pageKey}`);
        return stashed;
      }
    } catch {
      /* private mode — fall through to the first visible tab */
    }
    return null;
  });
  const current =
    visibleTabs.find((t) => t.key === activeKey) ?? visibleTabs[0] ?? null;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce"
        title={title}
        description={subtitle}
        tabs={visibleTabs.map((t) => ({ key: t.key, label: t.label }))}
        activeTab={current?.key}
        onTabChange={setActiveKey}
      />
      <div className="flex-1 overflow-y-auto bg-paper">
        {current ? (
          <GatedTabContent pageKey={pageKey} tabKey={current.key}>
            <EmbeddedChrome.Provider value={true}>{current.render()}</EmbeddedChrome.Provider>
          </GatedTabContent>
        ) : (
          <div className="p-6 text-sm text-ink-500">
            You don't have access to any sections in this area.
          </div>
        )}
      </div>
    </div>
  );
}
